// Inspiration Cards Workspace — 3D Coverflow
// Ported from studio: state -> store, CustomEvent -> eventBus
// Per-card markup lives in inspiration-card-html.js; this file owns the
// coverflow DOM state (build / transforms / swipe / dots) and card-list state.

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { catKey, _cardInnerHTML } from './inspiration-card-html.js'
import { DEFAULT_ON } from '../create/cards/state.js'

// ─── DOM persistence ──────────────────────────────────────────────────────────
let _workspaceEl = null
let _carouselEl = null
let _fingerprint = ''

// Stores last-computed transform values per card element.
// Avoids regex-parsing transform strings during swipe pointermove.
/** @type {Map<Element, {tx: number, ry: number, sc: number}>} */
const _cardTransforms = new Map()

// ─── Render ───────────────────────────────────────────────────────────────────

/**
 * Render (or incrementally update) the coverflow workspace into the docked
 * inspiration column; rebuilds the DOM only when the card-id fingerprint changed.
 *
 * Empty-state handoff (H-2026-09-12-128 2a): with no cards the panel shows the
 * official seed WALL (#inspirationWallCol, owned by inspiration-wall.js)
 * instead of the old "Select an image" placeholder; once an image's vision
 * cards exist they cover the wall. The wall column is display-toggled, never
 * rebuilt, so wall state (scroll/selection) survives card churn.
 */
export function renderInspirationWorkspace() {
  const container =
    document.getElementById('inspirationCardsCol') || document.getElementById('paneCards')
  if (!container) return
  const wallCol = document.getElementById('inspirationWallCol')

  const s = store.getState('inspirationCards')
  if (!s.cards.length) {
    container.innerHTML = ''
    if (wallCol) wallCol.hidden = false
    _workspaceEl = null
    _carouselEl = null
    return
  }
  if (wallCol) wallCol.hidden = true

  const fingerprint = s.cards.map((c) => c.id).join(',')
  const needsRebuild =
    !_workspaceEl || !container.contains(_workspaceEl) || _fingerprint !== fingerprint
  if (needsRebuild) _buildWorkspace(container, fingerprint)
  else _updateCards()
}

// ─── Build ────────────────────────────────────────────────────────────────────

function _buildWorkspace(container, fingerprint) {
  container.innerHTML = `
    <div class="inspiration-workspace">
      <div class="inspiration-carousel" id="iwCarousel"></div>
      <div class="iw-card-nav">
        <div class="carousel-dots" id="carouselDots"></div>
      </div>
    </div>`

  _workspaceEl = container.querySelector('.inspiration-workspace')
  _fingerprint = fingerprint
  _carouselEl = container.querySelector('#iwCarousel')

  // Click card -> jump to it
  _carouselEl.addEventListener('click', (e) => {
    const card = e.target.closest('.inspiration-card')
    if (!card) return
    const idx = parseInt(card.dataset.cardIdx)
    const s = store.getState('inspirationCards')
    if (idx !== s.activeIndex) navigateCardTo(idx)
  })

  _bindSwipe(_carouselEl)
  _rebuildCarousel()
  _updateCards()
}

// ─── Rebuild carousel ─────────────────────────────────────────────────────────

function _rebuildCarousel() {
  _cardTransforms.clear()
  const s = store.getState('inspirationCards')
  const carousel = document.getElementById('iwCarousel')
  if (!carousel) return

  carousel.innerHTML = ''
  s.cards.forEach((card, idx) => {
    const el = document.createElement('div')
    el.className = 'inspiration-card'
    el.dataset.cardIdx = idx
    el.innerHTML = _cardInnerHTML(card)

    const checkbox = el.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        const cur = store.getState('inspirationCards')
        const updated = { ...cur.checkedCards, [card.id]: checkbox.checked }
        store.setState('inspirationCards', { ...cur, checkedCards: updated })
      })
    }

    // Strength segmented control (ADR-005 2.5.0 — weighted cards). Sets
    // cardStrengths[id]; toggles active class in place (no full re-render).
    el.querySelectorAll('.cs-opt').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const cur = store.getState('inspirationCards')
        store.setState('inspirationCards', {
          ...cur,
          cardStrengths: { ...(cur.cardStrengths || {}), [card.id]: btn.dataset.str },
        })
        el.querySelectorAll('.cs-opt').forEach((b) => b.classList.toggle('active', b === btn))
      })
    })

    carousel.appendChild(el)
  })

  _carouselEl = carousel
}

// ─── Position update ──────────────────────────────────────────────────────────

const SPREAD = 160
const ROTATE_Y = 22
const SCALE_SIDE = 0.68

function _updateCards() {
  const s = store.getState('inspirationCards')
  if (!_carouselEl) return

  const activeIdx = s.activeIndex
  const total = s.cards.length
  const cardEls = _carouselEl.querySelectorAll('.inspiration-card')

  cardEls.forEach((el) => {
    const idx = parseInt(el.dataset.cardIdx)
    let offset = idx - activeIdx
    // shortest circular distance
    if (offset > total / 2) offset -= total
    if (offset < -total / 2) offset += total

    const isActive = offset === 0
    const absOff = Math.abs(offset)
    const visible = absOff <= 2

    el.classList.toggle('active', isActive)
    el.style.visibility = visible ? 'visible' : 'hidden'
    el.style.pointerEvents = visible ? 'auto' : 'none'

    const tx = offset * SPREAD
    const ry = offset * -ROTATE_Y
    const scale = isActive ? 1 : SCALE_SIDE - absOff * 0.04
    const opacity = isActive ? 1 : Math.max(0.25, 0.65 - absOff * 0.18)
    const z = 20 - absOff * 5

    el.style.transform = `translateX(${tx}px) rotateY(${ry}deg) scale(${scale})`
    el.style.opacity = opacity
    el.style.zIndex = z
    _cardTransforms.set(el, { tx, ry, sc: scale })
  })

  _updateDots()
}

function _updateDots() {
  const s = store.getState('inspirationCards')
  const dotsEl = document.getElementById('carouselDots')
  if (!dotsEl) return

  if (parseInt(dotsEl.dataset.count) !== s.cards.length) {
    dotsEl.dataset.count = s.cards.length
    dotsEl.innerHTML = ''
    s.cards.forEach((_, idx) => {
      const dot = document.createElement('span')
      dot.className = 'carousel-dot'
      dot.addEventListener('click', () => navigateCardTo(idx))
      dotsEl.appendChild(dot)
    })
  }

  dotsEl.querySelectorAll('.carousel-dot').forEach((dot, idx) => {
    dot.classList.toggle('active', idx === s.activeIndex)
  })
}

// ─── Navigation ───────────────────────────────────────────────────────────────

/**
 * Jump the coverflow to card `idx` and refresh transforms/dots.
 * @param {number} idx
 */
export function navigateCardTo(idx) {
  const s = store.getState('inspirationCards')
  store.setState('inspirationCards', { ...s, activeIndex: idx })
  _updateCards()
}

// ─── Touch / swipe ────────────────────────────────────────────────────────────

function _bindSwipe(carouselEl) {
  let startX = 0
  let swiping = false

  carouselEl.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return
    startX = e.clientX
    swiping = false
  })

  carouselEl.addEventListener('pointermove', (e) => {
    if (!(e.buttons & 1)) return
    const dx = e.clientX - startX
    if (!swiping && Math.abs(dx) > 12) swiping = true

    if (swiping) {
      _carouselEl.querySelectorAll('.inspiration-card').forEach((c) => {
        const stored = _cardTransforms.get(c)
        if (!stored) return
        c.style.transform = `translateX(${stored.tx + dx * 0.35}px) rotateY(${stored.ry}deg) scale(${stored.sc})`
      })
    }
  })

  carouselEl.addEventListener('pointerup', (e) => {
    if (!swiping) return
    const dx = e.clientX - startX
    if (Math.abs(dx) > 50) {
      const s = store.getState('inspirationCards')
      const next = (s.activeIndex + (dx < 0 ? 1 : -1) + s.cards.length) % s.cards.length
      navigateCardTo(next)
    } else {
      _updateCards()
    }
    swiping = false
  })

  carouselEl.addEventListener('pointercancel', () => {
    if (swiping) _updateCards()
    swiping = false
  })
}

// ─── Intent card filter ───────────────────────────────────────────────────────

eventBus.on(EVENTS.INTENT_FILTER, (detail) => {
  const s = store.getState('inspirationCards')
  const defaultOn = detail.defaultOnCategories // string[] | null
  if (defaultOn && s.cards.length) {
    const updatedChecked = { ...s.checkedCards }
    s.cards.forEach((card) => {
      if (card.id === 'custom') return
      updatedChecked[card.id] = defaultOn.includes(catKey(card.category))
    })
    store.setState('inspirationCards', { ...s, checkedCards: updatedChecked })
  }
  if (s.cards.length) {
    _rebuildCarousel()
    _updateCards()
  }
})

// ─── Reference cards (ADR-005 #10) ─────────────────────────────────────────────

/**
 * Merge vision/bundle-derived cards into the workspace as SELECTED inspiration
 * cards and re-render. Origin semantics (ADR-005 #11):
 *   - 'source'    — the A image's own bundle. Picking a NEW source REPLACES the
 *                   previous source's cards (they described the old pixels);
 *                   reference-fed cards survive the swap.
 *   - 'reference' — a B/reference pick. These ACCUMULATE across picks.
 *
 * The renderer reads `title`/`category`/`description`; vision returns
 * `name`/`card_type`/`prompt_text` — mapped here (title←name drift trap).
 *
 * @param {Array<Object>} rawCards
 * @param {'source'|'reference'} [origin='reference']
 * @returns {number} count of cards actually added (0 = all duplicates)
 */
export function addReferenceCards(rawCards, origin = 'reference') {
  const s = store.getState('inspirationCards') || { cards: [], checkedCards: {} }
  let existing = s.cards || []
  const droppedIds = []
  if (origin === 'source') {
    // A new source's bundle arrived — drop the previous source's cards first
    // (accumulating them stacks stale prompts from an image no longer in A).
    existing = existing.filter((c) => {
      if (c.origin !== 'source') return true
      droppedIds.push(String(c.id))
      return false
    })
  }
  const existingIds = new Set(existing.map((c) => String(c.id)))

  const normalized = (rawCards || [])
    .map((c) => ({
      id: c.id ?? c.card_id,
      title: c.title ?? c.name ?? 'Reference',
      category: c.category ?? c.card_type ?? 'Style',
      description: c.description ?? c.prompt_text ?? '',
      thumbnail: c.thumbnail ?? c.thumbnail_url ?? '',
      usage_count: c.usage_count ?? 0,
      origin,
      fromReference: true,
    }))
    .filter((c) => c.id != null && !existingIds.has(String(c.id)))

  if (!normalized.length && !droppedIds.length) return 0

  // Keep the trailing Custom card last if present.
  const customIdx = existing.findIndex((c) => c.id === 'custom')
  const head = customIdx >= 0 ? existing.slice(0, customIdx) : existing
  const tail = customIdx >= 0 ? existing.slice(customIdx) : []

  // Pre-select ONLY the reference cards whose category the current intent can
  // use (intent.compatible_categories / default_on_categories). Colorway is
  // compatible_categories="Color", so a reference contributes its colour card —
  // NOT its Scene/Style/Mood cards, which would drag the reference's SUBJECT
  // (e.g. "leaves") into the recolor. Falls back to selecting all when an intent
  // sets no restriction (e.g. Style Transfer wants the full style). why → 2026-06-18
  // "result looks like the reference, not the recoloured source".
  const intent = store.getState('creation.intentCard')
  const allowedRaw = intent?.default_on_categories || intent?.compatible_categories || ''
  const allowed = allowedRaw
    ? allowedRaw.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)
    : null
  const checkedCards = { ...(s.checkedCards || {}) }
  const cardStrengths = { ...(s.cardStrengths || {}) }
  // Replaced source cards take their selection/strength state with them.
  droppedIds.forEach((id) => { delete checkedCards[id]; delete cardStrengths[id] })
  normalized.forEach((c) => {
    const cat = catKey(c.category)
    // No intent restriction → only the DEFAULT_ON categories start checked
    // (ADR-005 #11 "default-on"); checking ALL cards drowned the prompt in every
    // extracted style at once.
    checkedCards[c.id] = allowed ? allowed.includes(cat) : DEFAULT_ON.has(cat)
  })

  store.setState('inspirationCards', {
    ...s,
    cards: [...head, ...normalized, ...tail],
    checkedCards,
    cardStrengths,
    activeIndex: head.length, // focus the first new reference card
    isDefaultCards: false,
  })

  _workspaceEl = null // force a structural rebuild (card list changed)
  renderInspirationWorkspace()
  return normalized.length
}

// ─── Custom style prompt ──────────────────────────────────────────────────────

/**
 * Read the trimmed value of the Custom card's free-text style prompt.
 * @returns {string} empty string when the card is absent or blank
 */
export function getCustomStylePrompt() {
  return document.getElementById('iwCustomPrompt')?.value?.trim() || ''
}
